/**
 * @file     flowIcons.tsx
 * @readme   ../README.md
 * @purpose  Inline-SVG icon set for the lead flows (bed/bath pickers, amenity pills, value-range benefits). Hand-rolled to match EntryStep's glyphs since the project ships no icon library. currentColor so callers control the tint.
 */
import type { ReactNode } from 'react';

const s = { stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, fill: 'none' };

export const FLOW_ICONS: Record<string, ReactNode> = {
  bed: (
    <>
      <path d="M3 18v-5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5" {...s} />
      <path d="M3 18h18M3 14h18M7 11V9a1 1 0 0 1 1-1h3v3" {...s} />
    </>
  ),
  bath: (
    <>
      <path d="M4 12h16v3a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4v-3Z" {...s} />
      <path d="M6 12V6a2 2 0 0 1 2-2 2 2 0 0 1 2 2M7 19l-1 2M18 19l1 2" {...s} />
    </>
  ),
  pool: (
    <>
      <path d="M3 16c1.5 0 1.5 1 3 1s1.5-1 3-1 1.5 1 3 1 1.5-1 3-1 1.5 1 3 1M3 20c1.5 0 1.5 1 3 1s1.5-1 3-1 1.5 1 3 1 1.5-1 3-1 1.5 1 3 1" {...s} />
      <path d="M8 16V5a2 2 0 0 1 4 0M16 16V5" {...s} />
    </>
  ),
  hot_tub: (
    <>
      <path d="M4 11h16v5a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3v-5Z" {...s} />
      <path d="M9 7c0-1-1-1-1-2M13 7c0-1-1-1-1-2M17 7c0-1-1-1-1-2" {...s} />
    </>
  ),
  gym: (
    <>
      <path d="M6.5 8v8M4 10v4M17.5 8v8M20 10v4M6.5 12h11" {...s} />
    </>
  ),
  transit: (
    <>
      <rect x="6" y="4" width="12" height="12" rx="2" {...s} />
      <path d="M6 12h12M9 20l1.5-2M15 20l-1.5-2" {...s} />
      <circle cx="9" cy="16" r="0.6" fill="currentColor" stroke="none" />
      <circle cx="15" cy="16" r="0.6" fill="currentColor" stroke="none" />
    </>
  ),
  parking: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" {...s} />
      <path d="M9 16V8h3.5a2.5 2.5 0 0 1 0 5H9" {...s} />
    </>
  ),
  renovated: (
    <>
      <path d="M14.5 5.5a3.5 3.5 0 0 0-4.6 4.6l-5 5a1.5 1.5 0 0 0 2.1 2.1l5-5a3.5 3.5 0 0 0 4.6-4.6l-2 2-2-2 2-2Z" {...s} />
    </>
  ),
  history: (
    <>
      <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1M3.5 4.5V9H8" {...s} />
      <path d="M12 8v4l3 2" {...s} />
    </>
  ),
  trending: (
    <>
      <path d="M3 17l6-6 4 4 8-8" {...s} />
      <path d="M15 7h6v6" {...s} />
    </>
  ),
  improve: (
    <>
      <path d="M3 21l3-1 9-9-2-2-9 9-1 3Z" {...s} />
      <path d="M14 6l2-2a2 2 0 0 1 3 3l-2 2-3-3Z" {...s} />
    </>
  ),
};

export function FlowIcon({ name, size = 20 }: { name: string; size?: number }) {
  const glyph = FLOW_ICONS[name];
  if (!glyph) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}>
      {glyph}
    </svg>
  );
}
